import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';
import NavigationButton from '@components/navigation/navigation-button/navigation-button.tsx';
import LoadingIndicator from '@components/core/loading-indicator/loading-indicator.tsx';
import ViewPanel from '@components/view/view-panel.tsx';
import ClassString from '@utils/class-string.ts';
import useScreenBreakpoint from '@hooks/use-screen-breakpoint.tsx';
import { ScreenBreakpoint } from '@/enums/screen-breakpoints.ts';
import navSidebarStore from '@store/nav-sidebar-store.ts';
import AppLogo from '@components/core/app-logo/app-logo.tsx';
import authStore from '@store/auth-store.ts';

const VerifyEmail = observer(function VerifyEmail() {
	const token = useLoaderData<string>();

	const screenBreakpoint = useScreenBreakpoint();
	const [fadeIn, setFadeIn] = useState(false);
	const [loading, setLoading] = useState(true);

	const [verificationSuccessful, setVerificationSuccessful] = useState(false);

	document.title = 'Verify Email | UGC Insight';

	const verifyEmail = async () => {
		try {
			const resp = await authStore.verifyEmail(token);

			if (resp.ok) {
				setVerificationSuccessful(true);
				void authStore.restoreSession();
			}
		} catch (_) {
			// Error handled elsewhere
		}

		setLoading(false);
	};

	useEffect(() => {
		setFadeIn(true);
		void verifyEmail();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			className={
				'w-screen h-dvh bg-green-100 dark:bg-gray-575 sm:bg-transparent dark:sm:bg-transparent grid place-items-center overflow-y-auto sm:bg-top-page-pattern sm:dark:bg-none bg-cover'
			}
		>
			<div
				className={
					'flex flex-col justify-center items-center gap-4 h-full p-4'
				}
			>
				<ViewPanel
					className={ClassString({
						static: 'max-w-md opacity-0 transition-opacity bg-transparent sm:bg-green-100 pb-8',
						dynamic: { 'opacity-100': fadeIn },
					})}
					options={{
						container: screenBreakpoint <= ScreenBreakpoint.SM,
					}}
				>
					<div
						className={ClassString({
							static: 'flex flex-col justify-center items-center px-4',
							dynamic: {
								'opacity-0': loading,
							},
						})}
					>
						<AppLogo
							className={ClassString({
								static: 'flex flex-col justify-center items-center dark:text-white pt-0 pb-4 select-none flex-1',
								dynamic: {
									'lg:hidden': navSidebarStore.minimal,
								},
							})}
						/>

						{!verificationSuccessful && (
							<div className={'mb-6'}>
								Email verification failed
							</div>
						)}

						{verificationSuccessful && (
							<>
								<div className={'mb-6'}>
									Email verified — you’re all set!
								</div>

								<NavigationButton
									path={'/overview'}
									title={'Continue'}
									className={
										'mt-2 w-full py-6 rounded-full max-w-80 justify-center bg-gray-600 text-white'
									}
									background
								/>
							</>
						)}
					</div>

					<div
						className={ClassString({
							static: 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none',
							dynamic: { 'opacity-0': !loading },
						})}
					>
						<LoadingIndicator />
					</div>
				</ViewPanel>
			</div>
		</div>
	);
});

export default VerifyEmail;
